import {
  JobStatus,
  LogsApiSystemColumns,
  MLEnum,
  Unit,
} from '../TypeScript/enums';
import { JobInfoMessage } from '../TypeScript/types';
import Viewer from 'viewerjs';

export const CIRCULAR_PROGRESS_SIZE = 18;

export const FADEOUT_TIME = 300; // ms

export const stepSizeOptions = {
  [Unit.Meter]: [
    {
      value: 0.05,
      display: '0.05 meters',
    },
    {
      value: 0.2,
      display: '0.2 meters',
    },
    {
      value: 1.0,
      display: '1.0 meter',
    },
  ],
  [Unit.Foot]: [
    {
      value: 0.03,
      display: '0.03 feet',
    },
    {
      value: 0.2,
      display: '0.2 feet',
    },
    {
      value: 1.0,
      display: '1.0 foot',
    },
  ],
};

export const fallbackStepSizeOptions = [0.05, 0.2, 1.0].map((value) => ({
  display: value,
  value,
}));

export const roqenetColors = {
  [`${MLEnum.roqenet}-Rubble Zone A`]: '#FFC202',
  [`${MLEnum.roqenet}-Rubble Zone B`]: '#FF9002',
  [`${MLEnum.roqenet}-Rubble Zone D`]: '#FF4F02',
  [`${MLEnum.roqenet}-Rubble Zone C`]: '#FF6602',
  // [`${MLEnum.roqenet}-Rubble Zone E`]: '#FF0000',
  [`${MLEnum.roqenet}-Intact`]: '#00ff00',
  [`${MLEnum.roqenet}-Mechanical Joint`]: '#ffff00',
  [`${MLEnum.roqenet}-Mechanical Rubble Zone`]: '#ffff00',
  [`${MLEnum.roqenet}-Joint`]: '#FF0000',
};

export const fcnetColors = {
  [`${MLEnum.fcnet}-Class 0`]: '#d0d0d0',
  [`${MLEnum.fcnet}-Class 1`]: '#17317a',
  [`${MLEnum.fcnet}-Class 2`]: '#4660a7',
  [`${MLEnum.fcnet}-Class 3`]: '#869bd3',
};

/**
 * An object mapping of various roqenet zones and fcnet classes to their description translation keys
 */
export const colorZoneDescriptions: Record<string, string> = {
  [`${MLEnum.roqenet}-Rubble Zone A`]: 'RUBBLE_ZONE_A',
  [`${MLEnum.roqenet}-Rubble Zone B`]: 'RUBBLE_ZONE_B',
  [`${MLEnum.roqenet}-Rubble Zone C`]: 'RUBBLE_ZONE_C',
  [`${MLEnum.roqenet}-Rubble Zone D`]: 'RUBBLE_ZONE_D',
  [`${MLEnum.roqenet}-Intact`]: 'INTACT',
  [`${MLEnum.roqenet}-Mechanical Joint`]: 'MECHANICAL_JOINT',
  [`${MLEnum.roqenet}-Mechanical Rubble Zone`]: 'MECHANICAL_RUBBLE_ZONE',
  [`${MLEnum.roqenet}-Joint`]: 'JOINT',
  [`${MLEnum.fcnet}-Class 0`]: 'CLASS0',
  [`${MLEnum.fcnet}-Class 1`]: 'CLASS1',
  [`${MLEnum.fcnet}-Class 2`]: 'CLASS2',
  [`${MLEnum.fcnet}-Class 3`]: 'CLASS3',
  'Empty Data': 'EMPTY_DATA',
};

export const IS_TEST_ENV: boolean =
  process.env.REACT_APP_ENVIRONMENT !== 'prod';

export const consultingEmail: string = 'consulting.support@alsglobal.com';

export const JOB_TYPES = {
  BOXYNET: 'litholens.run_boxynet',
  CLEAN_IMAGES: 'litholens.cleanimages',
  COLOR_CLUSTERING: 'litholens.run_color_clustering',
  COLOR_EXTRACTION: 'litholens.run_color_extraction',
  DESURVEY: 'litholens.desurvey',
  EXPORT_TEXTURE_REPORT: 'litholens.texture_report_export',
  FCNET: 'litholens.fcnet',
  FRACTURE_FREQUENCY: 'litholens.run_roqenet_ff',
  IMAGES_QA_QC: 'logs.find_inconsistencies',
  MERGE: 'logs.merge',
  MUNSELL_REPORT: 'litholens.munsell_report',
  PERCENTAGE_REPORT: 'litholens.percentage_report',
  PREDICT_RUN_PREDICTION: 'predict.run_prediction_2',
  PREDICT_RUN_CLASSIFICATION_TRAINING: 'predict.run_classification_training_2',
  PREDICT_RUN_REGRESSION_TRAINING: 'predict.run_regression_training_2',
  RQD: 'litholens.run_roqenet_rqd',
  ROQENET: 'litholens.roqenet',
  TEXTURE_REPORT: 'litholens.texture_report',
  VEINNET: 'litholens.veinnet',
  VEIN_FREQUENCY: 'litholens.generate_vein_frequency_report',
  SMOOTHING: 'logs.smoothing',
};

export const PREDICT_JOB_TYPES = {
  CLASSIFICATION: 'classification',
  REGRESSION: 'regression',
};

// Prediction-related constants for data smoothing
export const PREDICTION_CONSTANTS = {
  DEFAULT_PROB_THRESHOLD: 0.6,
  DEFAULT_SEARCH_DISTANCE: 6,
};

export const ESC_KEY = 'Escape';
export const UP_KEY = 'ArrowUp';
export const DOWN_KEY = 'ArrowDown';
export const LEFT_KEY = 'ArrowLeft';
export const RIGHT_KEY = 'ArrowRight';

export const TRANSPARENT_PIXEL =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

export const INTERNAL_CO: string = 'goldspot';

/**
 * Viewer.js config options (base)
 *
 * See documentation:
 * - [Viewer.js Site](https://fengyuanchen.github.io/viewerjs/)
 * - [Viewer.js Docs](https://github.com/fengyuanchen/viewerjs/tree/main?tab=readme-ov-file#viewerjs)
 * - [Example of customized toolbar](https://github.com/fengyuanchen/viewerjs/blob/main/docs/examples/custom-toolbar.html)
 */
export const VIEWER_OPTIONS: Viewer.Options = {
  backdrop: true,
  button: true,
  keyboard: false,
  loading: true,
  toolbar: {
    zoomIn: 1,
    zoomOut: 1,
    oneToOne: 1,
    reset: 1,
    prev: 1,
    next: 1,
    play: {
      show: 0,
    },
    rotateLeft: 0,
    rotateRight: 0,
    flipHorizontal: 0,
    flipVertical: 0,
  },
  transition: false,
};

export const BOX_NO_REGEX = /^BOX-\d{1,}(-\d{1,})?$/;

/** Default/initial value of zoom for visualizer */
export const DEFAULT_ZOOM = 50;

// Visualizer URL search params
export const DATA_SOURCE_KEY_DELIMITER = '::';
export const DATA_SOURCE_FIELD_DELIMITER = '|';
export const X_RANGE_FIELD_DELIMITER = '|';

// Visualizer param sources
export const VIS_SEARCH_PARAM_SOURCES = {
  LOGS_API_LOG: 'logs_api_',
  IMAGE_STRIPLOG: 'image_striplog_',
  FILE_STRIPLOG: 'file_striplog_',
  SHAPE_IMAGE_LOG: 'shape_image_',
};

// Visualizer param names
export const VIS_SEARCH_PARAM_NAMES = {
  DRILLHOLE: 'drillhole',
  SOURCE: 'source',
  MULTIPLE: 'multiple',
  ZOOM: 'zoom',
  X_RANGE: 'xrange',
  GLOBAL_RANGE: 'globalrange',
};

export const VIS_ZOOM_DEFAULT = 50;

export const BASIC_EXTRACTION_JOBS_PACKAGE_WITH_INCURRED_COST = [
  JOB_TYPES.MUNSELL_REPORT,
  JOB_TYPES.ROQENET,
];

export const NON_BASIC_EXTRACTION_JOBS_PACKAGE = [
  JOB_TYPES.COLOR_EXTRACTION,
  JOB_TYPES.COLOR_CLUSTERING,
  JOB_TYPES.PERCENTAGE_REPORT,
  JOB_TYPES.FCNET,
  JOB_TYPES.TEXTURE_REPORT,
  JOB_TYPES.VEINNET,
  JOB_TYPES.VEIN_FREQUENCY,
];

export const JOB_ALERT_MESSAGES: Record<string, string[]> = {
  ALERT_WARNING_BASIC_EXTRACTION_JOB:
    BASIC_EXTRACTION_JOBS_PACKAGE_WITH_INCURRED_COST,
  ALERT_WARNING_NON_BASIC_EXTRACTION_JOB: NON_BASIC_EXTRACTION_JOBS_PACKAGE,
};

export const CUSTOM_COLOR_JOBS_PACKAGES = [
  JOB_TYPES.COLOR_EXTRACTION,
  JOB_TYPES.COLOR_CLUSTERING,
  JOB_TYPES.PERCENTAGE_REPORT,
];

export const ML_JOBS_DEPENDENCIES = [
  {
    requiredJob: JOB_TYPES.COLOR_EXTRACTION,
    dependentJobs: [JOB_TYPES.COLOR_CLUSTERING],
  },
  {
    requiredJob: JOB_TYPES.COLOR_CLUSTERING,
    dependentJobs: [JOB_TYPES.PERCENTAGE_REPORT],
  },
  {
    requiredJob: JOB_TYPES.ROQENET,
    dependentJobs: [JOB_TYPES.RQD, JOB_TYPES.FRACTURE_FREQUENCY],
  },
  {
    requiredJob: JOB_TYPES.VEINNET,
    dependentJobs: [JOB_TYPES.VEIN_FREQUENCY],
  },
];

export const REFRESHABLE_JOB_STATUS_SET = new Set<string>([
  JobStatus.todo,
  JobStatus.requested,
  JobStatus.inProgress,
]);

export const QAQC_JOB_TYPES_SET = new Set<string>([JOB_TYPES.IMAGES_QA_QC]);

export const CLEANABLE_JOB_TYPES_SET = new Set<string>([
  JOB_TYPES.CLEAN_IMAGES,
]);

export const CONTINUABLE_JOB_TYPES_SET = new Set<string>([
  JOB_TYPES.COLOR_EXTRACTION,
  JOB_TYPES.COLOR_CLUSTERING,
]);

export const EXPORTABLE_JOB_TYPES_SET = new Set<string>([
  JOB_TYPES.TEXTURE_REPORT,
]);

export const VIEWABLE_JOB_TYPES_SET = new Set<string>([
  JOB_TYPES.RQD,
  JOB_TYPES.FCNET,
  JOB_TYPES.DESURVEY,
  JOB_TYPES.FRACTURE_FREQUENCY,
  JOB_TYPES.MERGE,
  JOB_TYPES.MUNSELL_REPORT,
  JOB_TYPES.PERCENTAGE_REPORT,
  JOB_TYPES.PREDICT_RUN_PREDICTION,
  JOB_TYPES.ROQENET,
  JOB_TYPES.SMOOTHING,
  JOB_TYPES.VEINNET,
  JOB_TYPES.VEIN_FREQUENCY,
]);

export const VIEWABLE_WITH_PARAMS_JOB_TYPES_SET = new Set<string>([
  JOB_TYPES.PREDICT_RUN_PREDICTION,
  JOB_TYPES.SMOOTHING,
]);

export const DOWNLOADABLE_JOB_TYPES_SET = new Set<string>([
  JOB_TYPES.PREDICT_RUN_PREDICTION,
  JOB_TYPES.SMOOTHING,
  JOB_TYPES.MERGE,
]);

export const VIEWABLE_METRICS_JOB_TYPES_SET = new Set<string>([
  JOB_TYPES.PREDICT_RUN_CLASSIFICATION_TRAINING,
  JOB_TYPES.PREDICT_RUN_REGRESSION_TRAINING,
]);

export const LOGS_DOWNLOADABLE_JOB_TYPES_SET = new Set<string>([
  JOB_TYPES.PREDICT_RUN_PREDICTION,
  JOB_TYPES.MERGE,
  JOB_TYPES.SMOOTHING,
]);

export const DEBUG_DOWLOADABLE_JOB_TYPES_SET = new Set<string>([
  JOB_TYPES.CLEAN_IMAGES,
  JOB_TYPES.PERCENTAGE_REPORT,
  JOB_TYPES.TEXTURE_REPORT,
  JOB_TYPES.PREDICT_RUN_CLASSIFICATION_TRAINING,
  JOB_TYPES.PREDICT_RUN_REGRESSION_TRAINING,
  JOB_TYPES.PREDICT_RUN_PREDICTION,
  JOB_TYPES.MERGE,
  JOB_TYPES.SMOOTHING,
]);

export const COPYABLE_JOB_TYPES_SET = new Set<string>([
  JOB_TYPES.PREDICT_RUN_CLASSIFICATION_TRAINING,
  JOB_TYPES.PREDICT_RUN_REGRESSION_TRAINING,
]);

export const INFO_MESSAGES: Record<string, JobInfoMessage> = {
  [JOB_TYPES.MUNSELL_REPORT]: 'REPORT_DESCRIPTION_STANDARD_COLOR',
  [JOB_TYPES.ROQENET]: 'REPORT_DESCRIPTION_ROQENET',
  [JOB_TYPES.RQD]: {
    info: 'INFO_FREE_BASIC_EXTRACTION_AFTER_JOB',
    depends_on: 'REPORT_NAME_ROQENET',
  },
  [JOB_TYPES.FRACTURE_FREQUENCY]: {
    info: 'INFO_FREE_BASIC_EXTRACTION_AFTER_JOB',
    depends_on: 'REPORT_NAME_ROQENET',
  },
  [JOB_TYPES.VEINNET]: 'REPORT_DESCRIPTION_VEINNET',
  [JOB_TYPES.TEXTURE_REPORT]: 'REPORT_DESCRIPTION_TEXTURE_REPORT',
  [JOB_TYPES.CLEAN_IMAGES]: 'REPORT_DESCRIPTION_CLEAN_IMAGES',
};

export const PrintDataKey = 'print-data';
export const PrintDataKeyV3 = 'print-data-v3';

export const DESCRIPTION_MAX_LENGTH = 250;

// Pattern for validating the subtype field in the object api
// Contains only lowercase letters (a-z),
// digits (0-9), hyphens (-), dots (.), or underscores (_),
// and is between 1 and 30 characters long {1,30}
export const PATTERN_SUBTYPE = /^[0-9a-z-._]{1,30}$/;

// Set of log schema properties to exclude when displaying
// to the user for selection
// (e.g. in prediction input column mapping or merging data)
export const excludeSchemaPropertiesSet = new Set<string>([
  LogsApiSystemColumns._id,
  LogsApiSystemColumns.createdAt,
  LogsApiSystemColumns.createdBy,
  LogsApiSystemColumns.drillholeId,
  LogsApiSystemColumns.fromDepth,
  LogsApiSystemColumns.logId,
  LogsApiSystemColumns.toDepth,
  LogsApiSystemColumns.updatedAt,
  LogsApiSystemColumns.updatedBy,
]);

export const JOB_TYPE_ARCHIVED_SUFFIX = '.archived';

export const DEPTH_MARKERS_OBJECT_TYPE = 'depth-markers';

export const TRAIN_MODEL_RATING_ACC = 'Acc';
export const TRAIN_MODEL_RATING_R2 = 'R²';

export const NON_SPATIAL_PLACEHOLDER_DRILLHOLE = 'placeholder drillhole';
export const NON_SPATIAL_PLACEHOLDER_DEPTH_VALUE = 1;

export const APP_LINKS = {
  d3:
    process.env.REACT_APP_ENVIRONMENT === 'prod'
      ? 'https://tessellation.als.app'
      : process.env.REACT_APP_ENVIRONMENT === 'test2'
        ? 'https://tessellation.staging.test2.als.app'
        : 'https://tessellation.staging.als.app',
};

export const appUrls: Record<string, string> = ((): Record<string, string> => {
  switch (process.env.REACT_APP_ENVIRONMENT) {
    case 'prod':
      return {
        home: 'https://als.app',
        admin: 'https://admin.app.goldspot.ca',
        litholens: 'https://litholens.als.app',
      };
    case 'test':
      return {
        home: 'https://staging.als.app',
        admin: 'https://admin.staging.app.goldspot.ca',
        litholens: 'https://litholens.staging.als.app',
      };
    case 'test2':
      return {
        home: 'https://staging.test2.als.app',
        admin: 'https://admin.staging.test2.als.app',
        litholens: 'https://litholens.staging.test2.als.app',
      };
    default:
      return {
        home: 'https://staging.als.app',
        admin: 'https://admin.staging.app.goldspot.ca',
        litholens: 'https://litholens.staging.als.app',
      };
  }
})();

export const APP_ALERT_AUTO_HIDE_SHORT = 5000;
export const APP_ALERT_AUTO_HIDE_LONG = 10000;
export const APP_ALERT_PROGRESS_INTERVAL = 300; // update every 300ms

export const APP_Z_INDEX = {
  VISUALIZER_ZOOM_PANEL: 20,
  VISUALIZER_ICON_BUTTON: 25,
};
